import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, SelectItem } from 'primeng/api';

import { BaseServices } from '../../kuba.services';
import { SdsArchiveFile } from './sds-archive-picker.component';
import { SdsEmployeeExposure, SdsHazardCategory, SdsHazardStatementRecord, SdsRecord, SdsRecordFileVersion, SdsSupplierName, SdsSupplierOption } from '../models/sds.model';
import { nationalIdentityNumberValidator } from '../validators/national-identity-number.validator';
import {
  casListValidator,
  joinCasNumbers,
  mergeCasNumbersWithinLimit,
  splitCasNumbers
} from '../validators/cas-number.validator';
import { SdsPdfScannerService, SdsPdfScanResult } from '../services/sds-pdf-scanner.service';
import {
  DEFAULT_SDS_UI_PICTOGRAMS,
  SdsApiService,
  SdsEligibleEditor,
  SdsHazardStatementDto,
  SdsReplacementCandidate,
  SdsReplacementInfo,
  SdsUiPictogramOption,
  SdsUpdateDocumentRequest
} from '../services/sds-api.service';

const QR_PRINT_CANVAS_SIZE = 720;
const QR_PRINT_JPEG_QUALITY = 0.82;

@Component({
  selector: 'sds-detail',
  templateUrl: 'sds-detail.component.html',
  styleUrls: ['sds-detail.component.css', './sds-theme.css'],
  providers: [],
  standalone: false
})
export class SdsDetailComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  record: SdsRecord | null = null;
  editMode = false;
  /** True when opened from the Archived Chemicals view — the record is read-only history. */
  readOnly = false;
  /** True when the route asked for edit mode directly (?edit=true). */
  private openInEditMode = false;
  editForm!: FormGroup;
  loading = false;

  pdfPreviewVisible = false;
  pdfPreviewUrl: SafeResourceUrl | null = null;
  pdfPreviewLoading = false;
  private pdfBlobUrl: string | null = null;
  qrCodeImageUrl = '';
  isGeneratingShareLink = false;
  isUploadingFileVersion = false;

  /** True while a PDF is being scanned for a CAS number. */
  scanningCasFromPdf = false;

  /** Business whose archive is browsed in the "add from archive" picker. */
  businessId = BaseServices.BusinessId;
  archivePickerVisible = false;

  /** Active business employees offered in the exposure picker. */
  employeeOptions: SelectItem[] = [];
  private employeesById = new Map<number, SdsEligibleEditor>();

  pictogramOptions: SdsUiPictogramOption[] = DEFAULT_SDS_UI_PICTOGRAMS.map(option => ({ ...option }));
  selectedPictograms: string[] = [];
  private pictogramCatalogLoaded = false;

  pictogramMap: Record<string, { label: string; ghs: string; svg: string }> =
    this.buildPictogramMap(DEFAULT_SDS_UI_PICTOGRAMS);

  suppliers: SdsSupplierOption[] = [];
  addSupplierVisible = false;
  newSupplierName = '';


  revealedNationalIdentityNumbers: Record<number, string> = {};
  revealDialogVisible = false;
  revealDialogLoading = false;
  revealDialogReason = '';
  revealDialogError: string | null = null;
  revealDialogExposureId: number | null = null;
  revealDialogExposure: SdsEmployeeExposure | null = null;
  revealDialogRevealedNumber: string | null = null;

  // ----- Replacement workflow state -----
  replacementInfo: SdsReplacementInfo | null = null;
  replacementLoading = false;
  replaceDialogVisible = false;
  replaceCandidateSuggestions: SdsReplacementCandidate[] = [];
  selectedReplacementCandidate: SdsReplacementCandidate | null = null;
  replaceReason = '';
  replaceSaving = false;
  replaceError: string | null = null;
  private replacementSearchRequestId = 0;

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private messageService: MessageService,
    private sdsApiService: SdsApiService,
    private pdfScanner: SdsPdfScannerService
  ) {}

  ngOnInit(): void {
    document.querySelector('body')!.classList.remove('opened');
    this.readOnly = this.route.snapshot.queryParamMap.get('readonly') === 'true';
    // Routed from the list's "Edit" action — skip the extra Edit click.
    this.openInEditMode = this.route.snapshot.queryParamMap.get('edit') === 'true';
    this.initForm();
    this.loadSuppliers();
    this.loadPictograms();
    this.loadRecord();
    this.refreshHazardCategoryLabels();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshHazardCategoryLabels();
      this.suppliers = this.suppliers.map(option => option.value === null
        ? { ...option, label: this.translate.instant('SDS_SELECT_MANUFACTURER') }
        : option);
    });
  }

  private refreshHazardCategoryLabels(): void {
    const keys = this.hazardCategoryKeys.map(o => o.labelKey);
    this.translate.get(keys).subscribe(translations => {
      this.hazardCategoryOptions = this.hazardCategoryKeys.map(o => ({
        label: translations[o.labelKey] || o.labelKey,
        value: o.value
      }));
    });
  }

  private initForm(): void {
    this.editForm = this.fb.group({
      ProductName: ['', Validators.required],
      Supplier: [null, Validators.required],
      CASNumbers: [<string[]>[], [Validators.required, casListValidator]],
      NoCasNumber: [false],
      HazardStatements: [<SdsHazardStatementDto[]>[]],
      Description: [''],
      KeyRisk: [''],
      Probability: [1],
      Consequence: [1],
      TaskActivity: [''],
      OtherControls: [''],
      RequiresExposureTracking: [false],
      HazardCategory: [0],
      EmployeeExposures: this.fb.array([])
    });

    this.editForm.get('NoCasNumber')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noCasNumber => {
      const casNumbers = this.editForm.get('CASNumbers')!;
      if (noCasNumber === true) {
        casNumbers.disable();
      } else {
        casNumbers.enable();
      }
    });

    this.editForm.get('RequiresExposureTracking')?.valueChanges.subscribe(requiresTracking => {
      if (requiresTracking === true && this.canAccessEmployeeExposurePii && this.editEmployeeExposures.length === 0) {
        this.addEmployeeExposure();
      }

      if (requiresTracking !== true && this.editEmployeeExposures.length > 0) {
        this.editEmployeeExposures.clear();
      }
    });
  }

  /** Translation keys resolved at runtime; refreshed on language change. */
  private readonly hazardCategoryKeys: { labelKey: string; value: SdsHazardCategory; }[] = [
    { labelKey: 'SDS_HAZARD_CATEGORY_NONE', value: SdsHazardCategory.None },
    { labelKey: 'SDS_HAZARD_CATEGORY_ASBESTOS', value: SdsHazardCategory.Asbestos },
    { labelKey: 'SDS_HAZARD_CATEGORY_CARCINOGENIC', value: SdsHazardCategory.Carcinogenic },
    { labelKey: 'SDS_HAZARD_CATEGORY_MUTAGENIC', value: SdsHazardCategory.Mutagenic },
    { labelKey: 'SDS_HAZARD_CATEGORY_REPROTOXIC', value: SdsHazardCategory.Reprotoxic },
    { labelKey: 'SDS_HAZARD_CATEGORY_LEAD', value: SdsHazardCategory.Lead },
    { labelKey: 'SDS_HAZARD_CATEGORY_BIOLOGICAL_AGENT', value: SdsHazardCategory.BiologicalAgent },
    { labelKey: 'SDS_HAZARD_CATEGORY_IONISING_RADIATION', value: SdsHazardCategory.IonisingRadiation },
    { labelKey: 'SDS_HAZARD_CATEGORY_OTHER_REGULATED', value: SdsHazardCategory.OtherRegulated }
  ];
  hazardCategoryOptions = this.hazardCategoryKeys.map(o => ({ label: o.labelKey, value: o.value }));
  readonly HazardCategory = SdsHazardCategory;

  /** 1–5 options for the risk-analysis matrix (Probability × Consequence). */
  readonly figureValue: SelectItem[] = [1, 2, 3, 4, 5].map(n => ({ label: String(n), value: n }));

  /** Live risk figure derived from the edit-mode Probability × Consequence. */
  get editRiskFigure(): { value: number; labelKey: string; color: string } {
    const probability = Number(this.editForm?.get('Probability')?.value) || 0;
    const consequence = Number(this.editForm?.get('Consequence')?.value) || 0;
    return this.riskFigureFor(probability, consequence);
  }

  /** Bands match the create wizard: ≤6 Low (green), 7–14 Medium (amber), ≥15 High (red). */
  private riskFigureFor(probability: number, consequence: number): { value: number; labelKey: string; color: string } {
    const value = probability * consequence;
    if (value <= 0) {
      return { value: 0, labelKey: '', color: '#9e9e9e' };
    }
    if (value <= 6) {
      return { value, labelKey: 'SDS_RISK_LOW', color: '#4ec55c' };
    }
    if (value >= 15) {
      return { value, labelKey: 'SDS_RISK_HIGH', color: '#e7644f' };
    }
    return { value, labelKey: 'SDS_RISK_MEDIUM', color: '#f0ac19' };
  }

  /** Derived Low/Medium/High band label, persisted as Status for backward compatibility. */
  private riskBandLabel(probability: number, consequence: number): string | null {
    switch (this.riskFigureFor(probability, consequence).labelKey) {
      case 'SDS_RISK_LOW':
        return 'Low';
      case 'SDS_RISK_MEDIUM':
        return 'Medium';
      case 'SDS_RISK_HIGH':
        return 'High';
      default:
        return null;
    }
  }

  /** Sensible starting P/C for a legacy record that only stored a Low/Medium/High band. */
  private matrixDefaultsForBand(band: string | null | undefined): { probability: number | null; consequence: number | null; } {
    switch ((band ?? '').trim().toLowerCase()) {
      case 'high':
        return { probability: 5, consequence: 3 };
      case 'medium':
        return { probability: 3, consequence: 3 };
      case 'low':
        return { probability: 1, consequence: 1 };
      default:
        return { probability: null, consequence: null };
    }
  }

  get canAccessEmployeeExposurePii(): boolean {
    return this.record?.EmployeeExposures != null;
  }

  get hazardCategoryValue(): SdsHazardCategory {
    return (this.editForm?.get('HazardCategory')?.value ?? SdsHazardCategory.None) as SdsHazardCategory;
  }

  get isAsbestos(): boolean {
    return this.hazardCategoryValue === SdsHazardCategory.Asbestos;
  }

  get requiresNationalIdentityNumber(): boolean {
    return this.hazardCategoryValue !== SdsHazardCategory.None;
  }

  /**
   * Calls the server-side reveal endpoint, which writes an audit-log entry
   * for every successful reveal (and every denial). Only call when the user
   * has explicitly clicked a reveal action and supplied a reason.
   */
  revealExposureNin(exposureId: number | null | undefined): void {
    if (!exposureId) return;

    this.revealDialogExposureId = exposureId;
    this.revealDialogExposure = this.record?.EmployeeExposures?.find(exposure => exposure.Id === exposureId) || null;
    this.revealDialogReason = '';
    this.revealDialogError = null;
    this.revealDialogRevealedNumber = this.revealDialogExposure
      ? this.getRevealedNationalIdentityNumber(this.revealDialogExposure)
      : this.revealedNationalIdentityNumbers[exposureId] || null;
    this.revealDialogVisible = true;
  }

  submitRevealNin(): void {
    if (!this.revealDialogExposureId || this.revealDialogLoading || this.revealDialogRevealedNumber) {
      return;
    }

    const trimmedReason = this.revealDialogReason.trim();
    if (trimmedReason.length < 10) {
      this.revealDialogError = this.translate.instant('SDS_REVEAL_NIN_REASON_TOO_SHORT');
      return;
    }

    this.revealDialogLoading = true;
    this.revealDialogError = null;

    this.sdsApiService.revealExposureNationalIdentityNumber(this.revealDialogExposureId, trimmedReason).subscribe({
      next: response => {
        const nationalIdentityNumber = response.NationalIdentityNumber || '';
        this.revealDialogLoading = false;

        if (!nationalIdentityNumber) {
          this.revealDialogError = this.translate.instant('SDS_REVEAL_NIN_DENIED');
          return;
        }

        const exposureId = response.ExposureId || this.revealDialogExposureId!;
        this.revealedNationalIdentityNumbers[exposureId] = nationalIdentityNumber;
        this.revealDialogRevealedNumber = nationalIdentityNumber;

        const recordExposure = this.record?.EmployeeExposures?.find(exposure => exposure.Id === exposureId);
        if (recordExposure) {
          recordExposure.NationalIdentityNumber = nationalIdentityNumber;
        }

        const row = this.editEmployeeExposures.controls.find(c => c.value?.Id === exposureId);
        if (row) {
          row.patchValue({
            NationalIdentityNumber: nationalIdentityNumber
          });
        }

        this.messageService.add({
          severity: 'success',
          summary: 'SDS',
          detail: this.translate.instant('SDS_REVEAL_NIN_SUCCESS')
        });
      },
      error: error => {
        this.revealDialogLoading = false;
        this.revealDialogError = this.getRevealErrorMessage(error);
      }
    });
  }

  closeRevealDialog(): void {
    if (this.revealDialogLoading) {
      return;
    }

    this.revealDialogVisible = false;
    this.revealDialogReason = '';
    this.revealDialogError = null;
    this.revealDialogExposureId = null;
    this.revealDialogExposure = null;
    this.revealDialogRevealedNumber = null;
  }

  getRevealedNationalIdentityNumber(exposure: SdsEmployeeExposure): string | null {
    if (!exposure?.Id) {
      return null;
    }

    return ( this.revealedNationalIdentityNumbers[exposure.Id] || exposure.NationalIdentityNumber || null
    );
  }

  private getRevealErrorMessage(error: any): string {
    if (error?.error?.Code === 'SDS_NIN_KEY_UNAVAILABLE') {
      return this.translate.instant('SDS_REVEAL_NIN_KEY_UNAVAILABLE');
    }

    return ( error?.error?.Message
      || error?.error?.message
      || error?.error?.detail
      || this.translate.instant('SDS_REVEAL_NIN_DENIED'));
  }

  get editEmployeeExposures(): FormArray {
    return this.editForm.get('EmployeeExposures') as FormArray;
  }

  get editRequiresExposureTracking(): boolean {
    return this.editForm?.get('RequiresExposureTracking')?.value === true;
  }

  private loadRecord(preferredFileVersionId?: number | null): void {
    const documentId = Number(this.route.snapshot.paramMap.get('id'));
    if (!documentId) {
      return;
    }

    this.loading = true;
    // In read-only mode (opened from the Archived tab) request the document even if
    // it's archived/soft-deleted — otherwise the backend returns nothing.
    this.sdsApiService.getDocumentRecord(documentId, this.readOnly).subscribe({
      next: record => {
        this.record = preferredFileVersionId === undefined
          ? record
          : this.sdsApiService.selectFileVersion(record, preferredFileVersionId);
        this.ensureSupplierOption(this.record.Supplier);
        this.syncPictogramSelectionFromRecord();
        this.patchFormWithRecord();
        this.ensureQrImage();
        if (this.pdfPreviewVisible) {
          this.openPdfPreview();
        }
        this.loadReplacementInfo();
        this.loading = false;

        if (this.openInEditMode && !this.readOnly && !this.editMode) {
          this.enableEdit();
        }
      },
      error: error => {
        this.loading = false;
        this.record = null;

        if (error?.status !== 404) {
          this.messageService.add({
            severity: 'error',
            summary: 'SDS',
            detail: 'Could not load the safety data sheet from the backend.'
          });
        }
      }
    });
  }

  private loadPictograms(): void {
    this.sdsApiService.getPictogramOptions().subscribe({
      next: options => {
        this.pictogramCatalogLoaded = true;
        this.refreshPictogramOptions(options);
      },
      error: error => {
        this.pictogramCatalogLoaded = false;
        this.refreshPictogramOptions(DEFAULT_SDS_UI_PICTOGRAMS);

        if (error?.status !== 404) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SDS',
            detail: 'Could not load pictograms from the backend. Showing fallback icons.'
          });
        }
      }
    });
  }

  private loadSuppliers(): void {
    this.sdsApiService.getCurrentBusinessSupplierNames().subscribe({
      next: names => {
        this.suppliers = this.buildSupplierOptions(names);
        if (this.record) {
          this.ensureSupplierOption(this.record.Supplier);
        }
      },
      error: error => {
        this.suppliers = this.buildSupplierOptions([]);
        if (this.record) {
          this.ensureSupplierOption(this.record.Supplier);
        }

        if (error?.status !== 404) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SDS',
            detail: this.translate.instant('SDS_MANUFACTURERS_LOAD_ERROR')
          });
        }
      }
    });
  }

  private buildSupplierOptions(names: SdsSupplierName[]): SdsSupplierOption[] {
    const byName = new Map<string, SdsSupplierOption>();

    for (const entry of names || []) {
      const name = (entry?.Name || '').trim();
      if (!name || byName.has(name.toLowerCase())) {
        continue;
      }

      byName.set(name.toLowerCase(), { label: name, value: name, isDefault: !!entry.IsDefault });
    }

    return [
      { label: this.translate.instant('SDS_SELECT_MANUFACTURER'), value: null, isDefault: false },
      ...[...byName.values()].sort((left, right) => left.label.localeCompare(right.label))
    ];
  }

  private ensureSupplierOption(name: string | null | undefined): void {
    const normalizedName = (name || '').trim();
    if (!normalizedName) {
      return;
    }

    const exists = this.suppliers.some(
      supplier => String(supplier.value || '').toLowerCase() === normalizedName.toLowerCase()
    );
    if (exists) {
      return;
    }

    this.suppliers = this.buildSupplierOptions([
      ...this.suppliers
        .filter(supplier => !!supplier.value)
        .map(supplier => ({ Name: String(supplier.value), IsDefault: supplier.isDefault })),
      { Name: normalizedName, IsDefault: false }
    ]);
  }

  openAddSupplier(): void {
    this.addSupplierVisible = true;
    this.newSupplierName = '';
  }

  saveNewSupplier(): void {
    const supplierName = this.newSupplierName.trim();
    if (!supplierName) {
      return;
    }

    this.ensureSupplierOption(supplierName);
    this.editForm.patchValue({ Supplier: supplierName });
    this.addSupplierVisible = false;
    this.newSupplierName = '';
  }

  private buildPictogramMap(
    options: SdsUiPictogramOption[]
  ): Record<string, { label: string; ghs: string; svg: string }> {
    return options.reduce((map, option) => {
      map[option.id] = {
        label: option.label,
        ghs: option.ghs,
        svg: option.svg
      };

      return map;
    }, {} as Record<string, { label: string; ghs: string; svg: string }>);
  }

  private refreshPictogramOptions(options: SdsUiPictogramOption[]): void {
    this.pictogramOptions = this.mergeRecordPictogramOptions(options);
    this.pictogramMap = this.buildPictogramMap(this.pictogramOptions);
  }

  private mergeRecordPictogramOptions(options: SdsUiPictogramOption[]): SdsUiPictogramOption[] {
    const mergedOptions = options.map(option => ({ ...option }));
    const optionsById = new Map(mergedOptions.map(option => [option.id, option]));

    for (const pictogramId of this.record?.Pictograms || []) {
      if (optionsById.has(pictogramId)) {
        continue;
      }

      const existingInfo = this.pictogramMap[pictogramId];
      const fallbackInfo = existingInfo || { label: pictogramId, ghs: '?', svg: '' };

      optionsById.set(pictogramId, {
        id: pictogramId,
        backendId: null,
        lookupKey: pictogramId,
        label: fallbackInfo.label,
        ghs: fallbackInfo.ghs,
        svg: fallbackInfo.svg
      });
    }

    return [...optionsById.values()];
  }

  private syncPictogramSelectionFromRecord(): void {
    this.selectedPictograms = [...new Set(this.record?.Pictograms || [])];
    this.refreshPictogramOptions(this.pictogramOptions);
  }

  private patchFormWithRecord(): void {
    if (!this.record) {
      return;
    }

    this.editForm.patchValue({
      ProductName: this.record.ProductName,
      Supplier: this.record.Supplier,
      CASNumbers: splitCasNumbers(this.record.CASNumber),
      NoCasNumber: this.record.NoCasNumber === true,
      HazardStatements: this.toHazardStatementPickerValue(this.record.HazardStatements),
      Description: this.record.Description || '',
      KeyRisk: this.record.KeyRisk || '',
      Probability: this.record.Probability
        ?? this.matrixDefaultsForBand(this.record.RiskFigure?.ValueText).probability,
      Consequence: this.record.Consequence
        ?? this.matrixDefaultsForBand(this.record.RiskFigure?.ValueText).consequence,
      TaskActivity: this.record.TaskActivity || '',
      OtherControls: this.record.OtherControls || '',
      RequiresExposureTracking: this.record.RequiresExposureTracking === true,
      HazardCategory: this.record.HazardCategory ?? SdsHazardCategory.None
    });

    this.patchExposureFormArray(this.record.EmployeeExposures || []);
  }

  enableEdit(): void {
    if (this.readOnly) {
      return;
    }
    this.editMode = true;
    this.syncPictogramSelectionFromRecord();
    if (this.canAccessEmployeeExposurePii) {
      this.loadBusinessEmployees();
    }
    if (this.editRequiresExposureTracking && this.canAccessEmployeeExposurePii && this.editEmployeeExposures.length === 0) {
      this.addEmployeeExposure();
    }
  }

  /** Load active business employees so the exposure picker can auto-fill name/email. */
  private loadBusinessEmployees(): void {
    if (this.employeeOptions.length > 0) {
      return;
    }
    this.sdsApiService.getBusinessEmployees(BaseServices.BusinessId).subscribe({
      next: employees => {
        this.employeesById = new Map((employees || []).map(e => [e.UserId, e]));
        this.employeeOptions = (employees || []).map(e => ({
          label: e.Email ? `${e.Name} (${e.Email})` : e.Name,
          value: e.UserId
        }));
      },
      error: () => {
        this.employeeOptions = [];
        this.employeesById = new Map();
      }
    });
  }

  /**
   * When an employee is picked, auto-fill the free-text name/email fields. The
   * national identity number is deliberately left untouched — it is sensitive PII
   * and must be entered manually.
   */
  onEmployeeSelected(index: number, userId: number | null): void {
    const group = this.editEmployeeExposures.at(index) as FormGroup;
    if (!group) {
      return;
    }

    const employee = userId != null ? this.employeesById.get(userId) : undefined;
    if (!employee) {
      return;
    }

    const { firstName, lastName } = this.splitEmployeeName(employee.Name);
    group.patchValue({
      FirstName: firstName,
      LastName: lastName,
      Email: employee.Email ?? ''
    });
  }

  /** Split a full display name into first name (first token) and last name (remainder). */
  private splitEmployeeName(fullName: string | null | undefined): { firstName: string; lastName: string; } {
    const name = (fullName ?? '').trim();
    if (!name) {
      return { firstName: '', lastName: '' };
    }
    const firstSpace = name.indexOf(' ');
    if (firstSpace < 0) {
      return { firstName: name, lastName: '' };
    }
    return {
      firstName: name.slice(0, firstSpace).trim(),
      lastName: name.slice(firstSpace + 1).trim()
    };
  }

  cancelEdit(): void {
    this.editMode = false;
    this.syncPictogramSelectionFromRecord();
    this.patchFormWithRecord();
  }

  saveEdit(): void {
    if (!this.record) {
      return;
    }

    if (!this.editForm.valid) {
      this.editForm.markAllAsTouched();
      return;
    }

    // Pictograms are optional — a chemical that carries no GHS classification
    // is saved with an empty pictogram set, which clears any earlier selection.
    const pictogramIds = this.getSelectedPictogramIds();
    if (pictogramIds.length !== this.selectedPictograms.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'SDS',
        detail: this.pictogramCatalogLoaded
          ? 'Some selected pictograms could not be matched to backend ids yet.'
          : 'The pictogram catalog has not loaded from the backend yet.'
      });
      return;
    }

    const values = this.editForm.value;
    if (values.RequiresExposureTracking && this.canAccessEmployeeExposurePii && this.editEmployeeExposures.length === 0) {
      this.addEmployeeExposure();
      this.editForm.markAllAsTouched();
      return;
    }

    const selectedHazardStatements = (values.HazardStatements || []) as SdsHazardStatementDto[];

    const payload: SdsUpdateDocumentRequest = {
      Product: values.ProductName,
      CASNumber: values.NoCasNumber ? '' : joinCasNumbers(values.CASNumbers as string[]),
      NoCasNumber: values.NoCasNumber === true,
      Supplier: values.Supplier,
      HazardStatementCodes: selectedHazardStatements.map(statement => statement.Code),
      Risk: values.KeyRisk || null,
      Exposure: values.Description || null,
      Control: values.OtherControls || null,
      Usage: values.TaskActivity || null,
      // Risk analysis (5×5 matrix). Probability/Consequence are persisted; Status
      // keeps the derived Low/Medium/High band for the list/dashboard.
      Probability: values.Probability ?? null,
      Consequence: values.Consequence ?? null,
      Status: this.riskBandLabel(Number(values.Probability) || 0, Number(values.Consequence) || 0),
      RequiresExposureTracking: values.RequiresExposureTracking === true,
      HazardCategory: values.HazardCategory ?? SdsHazardCategory.None,
      PictogramIds: pictogramIds
    };

    if (this.canAccessEmployeeExposurePii) {
      payload.EmployeeExposures = values.RequiresExposureTracking === true
        ? this.sdsApiService.mapEmployeeExposuresToDto(values.EmployeeExposures)
        : [];
    }

    this.loading = true;
    const selectedFileVersionId = this.record.SelectedFileVersionId ?? null;
    this.sdsApiService.updateDocument(this.record.Id, payload).subscribe({
      next: document => {
        const updatedRecord = this.sdsApiService.mapDocumentToRecord(document);
        this.record = this.sdsApiService.selectFileVersion(updatedRecord, selectedFileVersionId);

        this.editMode = false;
        this.syncPictogramSelectionFromRecord();
        this.patchFormWithRecord();
        this.ensureQrImage();
        this.loading = false;

        this.translate.get('SDS_UPDATE_SUCCESS', { name: this.record?.ProductName }).subscribe(message => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('SAVE'),
            detail: message
          });
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('SAVE'),
          detail: 'Could not update the safety data sheet.'
        });
      }
    });
  }

  togglePictogram(id: string): void {
    const index = this.selectedPictograms.indexOf(id);
    if (index >= 0) {
      this.selectedPictograms.splice(index, 1);
    } else {
      this.selectedPictograms.push(id);
    }
  }

  isPictogramSelected(id: string): boolean {
    return this.selectedPictograms.includes(id);
  }

  getPictogramInfo(id: string): { label: string; ghs: string; svg: string } {
    return this.pictogramMap[id] || { label: id, ghs: '?', svg: '' };
  }

  private getSelectedPictogramIds(): number[] {
    const ids = this.selectedPictograms
      .map(id => this.pictogramOptions.find(option => option.id === id)?.backendId || null)
      .filter((id): id is number => id !== null && id > 0);

    return [...new Set(ids)];
  }

  formatHazardStatement(statement: SdsHazardStatementDto): string {
    return this.sdsApiService.formatHazardStatement(statement, this.translate?.currentLang());
  }

  /**
   * Adapts the chips-friendly record shape stored on {@link SdsRecord} back
   * to the picker's DTO shape so the user sees the same chips they had
   * before entering edit mode.
   */
  private toHazardStatementPickerValue(
    statements: SdsHazardStatementRecord[] | null | undefined
  ): SdsHazardStatementDto[] {
    return (statements || []).map(statement => ({
      Id: statement.Id,
      Code: statement.Code,
      StatementType: statement.StatementType,
      StatementEn: statement.StatementEn,
      StatementNb: statement.StatementNb ?? null,
      StatementNn: statement.StatementNn ?? null,
      HazardGroup: null,
      Subcategory: null,
      IsCombined: statement.IsCombined,
      SortOrder: statement.SortOrder
    }));
  }

  get fileVersionOptions(): SelectItem[] {
    return (this.record?.FileVersions || []).map(fileVersion => ({
      label: this.buildFileVersionLabel(fileVersion),
      value: fileVersion.Id
    }));
  }

  get hasUploadedPdf(): boolean {
    return !!this.record?.SdsFileData || !!this.record?.SdsFileUrl;
  }

  get hasPdf(): boolean {
    return !!this.record?.SdsFileData || !!this.record?.SdsFileUrl;
  }

  onFileVersionChange(fileVersionId: number | null): void {
    if (!this.record) {
      return;
    }

    this.record = this.sdsApiService.selectFileVersion(this.record, fileVersionId);
    this.ensureQrImage();

    if (this.pdfPreviewVisible) {
      this.openPdfPreview();
    }
  }

  async openPdfPreview(): Promise<void> {
    if (!this.record) {
      return;
    }

    this.revokePdfBlobUrl();
    this.pdfPreviewUrl = null;
    this.pdfPreviewVisible = true;

    if (this.record.SdsFileData) {
      const blob = this.base64ToBlob(this.record.SdsFileData, 'application/pdf');
      this.pdfBlobUrl = URL.createObjectURL(blob);
      this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.pdfBlobUrl}#zoom=100`);
      this.ensureQrImage();
      return;
    }

    if (!this.record.SdsFileUrl && !this.record.SelectedFileVersionId) {
      this.ensureQrImage();
      return;
    }

    try {
      this.pdfPreviewLoading = true;
      const file = await this.sdsApiService
        .getDocumentFile(
          this.record.Id,
          this.record.SelectedFileVersionId,
          this.record.SdsFileName,
          'application/pdf'
        )
        .toPromise();

      if (!file) {
        throw new Error('The selected SDS file could not be loaded.');
      }

      this.pdfBlobUrl = URL.createObjectURL(file);
      this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.pdfBlobUrl}#zoom=100`);
    } catch (_) {
      this.messageService.add({
        severity: 'error',
        summary: 'SDS',
        detail: this.translate.instant('SDS_PDF_NOT_AVAILABLE')
      });
    } finally {
      this.pdfPreviewLoading = false;
    }

    this.ensureQrImage();
  }

  closePdfPreview(): void {
    this.pdfPreviewVisible = false;
    this.pdfPreviewLoading = false;
  }

  async onFileUploadSelected(event: Event): Promise<void> {
    if (!this.record) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    const isPdf = (file.type || '').toLowerCase() === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      this.messageService.add({
        severity: 'warn',
        summary: 'SDS',
        detail: this.translate.instant('SDS_UPLOAD_VERSION_PDF_ONLY')
      });
      if (input) {
        input.value = '';
      }
      return;
    }

    // Mine the new PDF for a CAS number (does not block the upload).
    this.scanPdfForCas(file);

    try {
      this.isUploadingFileVersion = true;
      await this.sdsApiService.uploadDocumentFile(this.record.Id, file).toPromise();
      this.loadRecord();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('SDS_UPLOAD_TITLE'),
        detail: this.translate.instant('SDS_UPLOAD_VERSION_SUCCESS')
      });
    } catch (_) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SDS_UPLOAD_TITLE'),
        detail: this.translate.instant('SDS_UPLOAD_VERSION_FAILED')
      });
    } finally {
      this.isUploadingFileVersion = false;
      if (input) {
        input.value = '';
      }
    }
  }

  /** Re-scan the record's already-saved PDF for CAS numbers (edit mode). */
  async detectCasFromSavedPdf(): Promise<void> {
    if (!this.record) {
      return;
    }
    this.scanningCasFromPdf = true;
    try {
      const file = await this.getSavedPdfFile();
      if (!file) {
        this.messageService.add({
          severity: 'info',
          summary: 'SDS',
          detail: this.translate.instant('SDS_PDF_NOT_AVAILABLE')
        });
        return;
      }
      const data = await this.pdfScanner.scanDocument(file);
      this.applyCasScanResult(data.cas);
      this.applyHStatementCodes(data.hStatementCodes);
    } finally {
      this.scanningCasFromPdf = false;
    }
  }

  /** Fetch the saved SDS PDF as a Blob (embedded base64 first, else backend). */
  private async getSavedPdfFile(): Promise<Blob | null> {
    if (!this.record) {
      return null;
    }
    if (this.record.SdsFileData) {
      return this.base64ToBlob(this.record.SdsFileData, 'application/pdf');
    }
    if (!this.record.SdsFileUrl && !this.record.SelectedFileVersionId) {
      return null;
    }
    const file = await this.sdsApiService
      .getDocumentFile(
        this.record.Id,
        this.record.SelectedFileVersionId,
        this.record.SdsFileName,
        'application/pdf'
      )
      .toPromise();
    return file ?? null;
  }

  /** Scan a PDF File/Blob and surface CAS results (auto-fill / picker / toast). */
  private async scanPdfForCas(file: Blob): Promise<void> {
    if (!this.pdfScanner.isPdf(file as File)) {
      return;
    }
    this.scanningCasFromPdf = true;
    try {
      const data = await this.pdfScanner.scanDocument(file);
      this.applyCasScanResult(data.cas);
      this.applyHStatementCodes(data.hStatementCodes);
    } finally {
      this.scanningCasFromPdf = false;
    }
  }

  private applyCasScanResult(result: SdsPdfScanResult): void {
    const { candidates, outcome } = result;
    if (candidates.length === 0) {
      // Distinguish "couldn't read text" (scanned image / unreadable) from
      // "read the PDF but it has no CAS number".
      this.messageService.add({
        severity: outcome === 'no-text' ? 'warn' : 'info',
        summary: 'SDS',
        detail: this.translate.instant(outcome === 'no-text' ? 'SDS_CAS_NO_TEXT' : 'SDS_CAS_NONE')
      });
      return;
    }

    // A document can hold several CAS numbers — add every valid one found,
    // de-duplicated and keeping anything already on the record.
    const { added, skipped } = this.mergeCasNumbers(candidates);

    if (added.length > 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'SDS',
        detail: added.length === 1
          ? this.translate.instant('SDS_CAS_DETECTED', { cas: added[0] })
          : this.translate.instant('SDS_CAS_MULTIPLE_ADDED', { count: added.length })
      });
    }

    if (skipped > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'SDS',
        detail: this.translate.instant('SDS_CAS_TOO_MANY_SKIPPED', { count: skipped })
      });
    }
  }

  /**
   * Adds the scanned CAS numbers to the chips control, skipping duplicates and
   * anything that would push the joined list past the length the API stores.
   * Returns the numbers actually added and how many were left out.
   */
  private mergeCasNumbers(found: string[]): { added: string[]; skipped: number; } {
    if (this.editForm.get('NoCasNumber')?.value === true) {
      return { added: [], skipped: 0 };
    }
    const control = this.editForm.get('CASNumbers');
    const existing: string[] = Array.isArray(control?.value) ? control!.value : [];
    const { merged, added, skipped } = mergeCasNumbersWithinLimit(existing, found);

    control?.setValue(merged);
    control?.markAsDirty();
    control?.markAsTouched();
    return { added, skipped: skipped.length };
  }

  /** Resolve H/EUH codes scanned from the PDF and add them to the hazard-statement picker. */
  private applyHStatementCodes(codes: string[]): void {
    if (!codes.length) {
      return;
    }
    this.sdsApiService.resolveHazardStatementsByCodes(codes).subscribe(statements => {
      if (!statements.length) {
        return;
      }
      this.mergeHazardStatements(statements);
      this.messageService.add({
        severity: 'success',
        summary: 'SDS',
        detail: this.translate.instant('SDS_HSTATEMENT_DETECTED', { count: statements.length })
      });
    });
  }

  private mergeHazardStatements(found: SdsHazardStatementDto[]): void {
    const control = this.editForm.get('HazardStatements');
    const existing: SdsHazardStatementDto[] = Array.isArray(control?.value) ? control!.value : [];
    const seen = new Set(existing.map(s => s.Id));
    const merged = [...existing];
    for (const statement of found) {
      if (!seen.has(statement.Id)) {
        merged.push(statement);
        seen.add(statement.Id);
      }
    }
    control?.setValue(merged);
    control?.markAsDirty();
    control?.markAsTouched();
  }

  openArchivePicker(): void {
    this.archivePickerVisible = true;
  }

  async onArchiveFileSelected(file: SdsArchiveFile): Promise<void> {
    if (!this.record) {
      return;
    }

    try {
      this.isUploadingFileVersion = true;
      await this.sdsApiService.addDocumentFileFromArchive(this.record.Id, file.id).toPromise();
      this.loadRecord();
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('SDS_UPLOAD_TITLE'),
        detail: this.translate.instant('SDS_UPLOAD_VERSION_SUCCESS')
      });
    } catch (_) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SDS_UPLOAD_TITLE'),
        detail: this.translate.instant('SDS_UPLOAD_VERSION_FAILED')
      });
    } finally {
      this.isUploadingFileVersion = false;
    }
  }

  async generateQrForRecord(): Promise<void> {
    if (!this.record) {
      return;
    }

    try {
      this.isGeneratingShareLink = true;
      if (!this.record.SdsFileUrl) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('SDS_QR_CODE'),
          detail: this.translate.instant('SDS_QR_NO_SHAREABLE_PDF')
        });
        return;
      }

      const response = await this.sdsApiService
        .createResolvedShareLink(this.record.Id, {
          FileVersionId: this.record.SelectedFileVersionId ?? null
        })
        .toPromise();

      if (!response) {
        return;
      }

      if (!response.ShareUrl) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('SDS_QR_CODE'),
          detail: this.translate.instant('SDS_QR_GENERATE_FAILED')
        });
        return;
      }

      this.record = this.sdsApiService.updateRecordFileVersionShare(
        this.record,
        response.FileVersionId,
        response.ShareUrl,
        response.ExpiresOnUtc,
        response.ShareLinkId
      );
      this.qrCodeImageUrl = this.buildQrImageUrl(this.record.QrShareUrl || '');
    } catch (_) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SDS_QR_CODE'),
        detail: this.translate.instant('SDS_QR_GENERATE_FAILED')
      });
    } finally {
      this.isGeneratingShareLink = false;
    }
  }

  private ensureQrImage(): void {
    if (!this.record?.QrShareUrl) {
      this.qrCodeImageUrl = '';
      return;
    }

    this.qrCodeImageUrl = this.buildQrImageUrl(this.record.QrShareUrl);
  }

  private buildQrImageUrl(url: string): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(url)}`;
  }

  private buildFileVersionLabel(fileVersion: SdsRecordFileVersion): string {
    const labelDate = fileVersion.UploadedOn || fileVersion.CreatedOn;
    const formattedDate = labelDate instanceof Date && !Number.isNaN(labelDate.getTime())
      ? labelDate.toLocaleDateString('en-GB')
      : '';
    const currentSuffix = fileVersion.IsCurrent
      ? ` (${this.translate.instant('SDS_CURRENT_VERSION_SHORT')})`
      : '';
    return formattedDate
      ? `${fileVersion.FileName} - ${formattedDate}${currentSuffix}`
      : `${fileVersion.FileName}${currentSuffix}`;
  }

  openShareUrl(): void {
    if (!this.record?.QrShareUrl) {
      return;
    }

    window.open(this.record.QrShareUrl, '_blank', 'noopener');
  }

  copyShareUrl(): void {
    if (!this.record?.QrShareUrl) {
      return;
    }

    navigator.clipboard?.writeText(this.record.QrShareUrl);
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('SDS_COPIED'),
      detail: this.translate.instant('SDS_SHARE_LINK_COPIED')
    });
  }

  async printQrA4(): Promise<void> {
    if (!this.record) {
      return;
    }

    if (!this.record.QrShareUrl) {
      await this.generateQrForRecord();
    }

    if (!this.record?.QrShareUrl) {
      return;
    }

    const qrImageUrl = this.buildQrImageUrl(this.record.QrShareUrl);
    const brandedQrDataUrl = await this.buildBrandedQrDataUrl(qrImageUrl);
    const title = this.record.ProductName || 'SDS QR';
    const fileName = this.record.SdsFileName || '';

    this.printQrImage(brandedQrDataUrl, title, fileName);
  }

  addEmployeeExposure(): void {
    this.editEmployeeExposures.push(this.createEmployeeExposureGroup());
  }

  removeEmployeeExposure(index: number): void {
    this.editEmployeeExposures.removeAt(index);
  }

  private patchExposureFormArray(exposures: SdsEmployeeExposure[]): void {
    this.editEmployeeExposures.clear();
    for (const exposure of exposures) {
      this.editEmployeeExposures.push(this.createEmployeeExposureGroup(exposure));
    }
  }

  private createEmployeeExposureGroup(exposure?: SdsEmployeeExposure): FormGroup {
    return this.fb.group({
      Id: [exposure?.Id ?? null],
      EmployeeUserId: [exposure?.EmployeeUserId ?? null],
      FirstName: [exposure?.FirstName || ''],
      LastName: [exposure?.LastName || ''],
      Email: [exposure?.Email || '', Validators.email],
      Address: [exposure?.Address || ''],
      // Plaintext NIN. Empty when reading existing rows; the masked form is held
      // separately for display. If the user types a new value here it overwrites.
      NationalIdentityNumber: ['', [nationalIdentityNumberValidator]],
      NationalIdentityNumberMasked: [exposure?.NationalIdentityNumberMasked || null],
      MedicalExamDate: [this.parseExposureDate(exposure?.MedicalExamDate)],
      MedicalExamDoctorName: [exposure?.MedicalExamDoctorName || ''],
      ExposureStartDate: [this.parseExposureDate(exposure?.ExposureStartDate), Validators.required],
      ExposureEndDate: [this.parseExposureDate(exposure?.ExposureEndDate)],
      LegalHold: [exposure?.LegalHold === true]
    });
  }

  private parseExposureDate(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private printQrImage(imageDataUrl: string, title: string, fileName: string = ''): void {
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';

    document.body.appendChild(printFrame);

    const printWindow = printFrame.contentWindow;
    const printDocument = printFrame.contentDocument || printWindow?.document;

    if (!printWindow || !printDocument) {
      document.body.removeChild(printFrame);
      return;
    }

    const escapedTitle = this.escapeHtml(title);
    const escapedFileName = this.escapeHtml(fileName);

    printWindow.onafterprint = () => {
      setTimeout(() => {
        if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      }, 500);
    };

    printDocument.open();
    printDocument.write(`
<!doctype html>
<html>
  <head>
    <title>${escapedTitle}</title>
    <style>
      @page { size: A4; margin: 0; }
      html,
      body {
        width: 100%;
        height: 100%;
        margin: 0;
      }
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: Arial, sans-serif;
      }
      .qr-title {
        margin: 0 0 4mm;
        font-size: 14pt;
        font-weight: bold;
        text-align: center;
      }
      img {
        width: 170mm;
        height: 170mm;
        object-fit: contain;
      }
      .qr-file-name {
        margin-top: 4mm;
        font-size: 11pt;
        text-align: center;
        word-break: break-all;
      }
    </style>
  </head>
  <body>
    <div class="qr-title">${escapedTitle}</div>
    <img src="${imageDataUrl}" alt="${escapedTitle}" onload="setTimeout(function(){ window.focus(); window.print(); }, 50)" />
    ${escapedFileName ? `<div class="qr-file-name">${escapedFileName}</div>` : ''}
  </body>
</html>`);
    printDocument.close();
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, char => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return entities[char];
    });
  }

  private async buildBrandedQrDataUrl(qrImageUrl: string): Promise<string> {
    const [qr, logo] = await Promise.all([
      this.loadImage(qrImageUrl),
      this.loadImage('assets/images/KubaLogo.png')
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = QR_PRINT_CANVAS_SIZE;
    canvas.height = QR_PRINT_CANVAS_SIZE;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(qr, 0, 0, canvas.width, canvas.height);

    const circleDiameter = Math.round(QR_PRINT_CANVAS_SIZE * 0.22);
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    ctx.beginPath();
    ctx.arc(cx, cy, circleDiameter / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    const logoMax = Math.round(QR_PRINT_CANVAS_SIZE * 0.14);
    const logoAspect = logo.width / logo.height || 1;
    let drawWidth = logoMax;
    let drawHeight = logoMax;

    if (logoAspect > 1) {
      drawHeight = logoMax / logoAspect;
    } else {
      drawWidth = logoMax * logoAspect;
    }

    ctx.drawImage(logo, cx - drawWidth / 2, cy - drawHeight / 2, drawWidth, drawHeight);
    return canvas.toDataURL('image/jpeg', QR_PRINT_JPEG_QUALITY);
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const imageUrl = new URL(src, document.baseURI);
      if (imageUrl.origin !== window.location.origin) {
        image.crossOrigin = 'anonymous';
      }
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Unable to load image: ${imageUrl.href}`));
      image.src = imageUrl.href;
    });
  }

  async downloadPdf(): Promise<void> {
    if (!this.record) {
      return;
    }

    if (this.record.SdsFileData) {
      const a = document.createElement('a');
      a.href = this.record.SdsFileData;
      a.download = this.record.SdsFileName || 'SDS_Document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    if (this.record.SdsFileUrl || this.record.SelectedFileVersionId) {
      try {
        const file = await this.sdsApiService
          .getDocumentFile(
            this.record.Id,
            this.record.SelectedFileVersionId,
            this.record.SdsFileName,
            'application/pdf',
            true
          )
          .toPromise();

        if (!file) {
          throw new Error('The selected SDS file could not be downloaded.');
        }

        this.triggerFileDownload(file);
      } catch (_) {
        this.messageService.add({
          severity: 'error',
          summary: 'SDS',
          detail: this.translate.instant('SDS_PDF_NOT_AVAILABLE')
        });
      }
    }
  }

  private triggerFileDownload(file: File): void {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name || 'SDS_Document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private revokePdfBlobUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
      this.pdfPreviewUrl = null;
    }
  }

  private base64ToBlob(dataUrl: string, contentType: string): Blob {
    const base64 = dataUrl.split(',')[1];
    const byteChars = atob(base64);
    const byteArrays: BlobPart[] = [];

    for (let offset = 0; offset < byteChars.length; offset += 512) {
      const slice = byteChars.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  }

  ngOnDestroy(): void {
    this.revokePdfBlobUrl();
  }

  // ----- Replacement workflow -----

  private loadReplacementInfo(): void {
    if (!this.record) {
      return;
    }
    this.replacementLoading = true;
    this.sdsApiService.getReplacementInfo(BaseServices.BusinessId, this.record.Id).subscribe({
      next: info => {
        this.replacementInfo = info;
        this.replacementLoading = false;
      },
      error: () => {
        this.replacementInfo = null;
        this.replacementLoading = false;
      }
    });
  }

  /**
   * In a chain A → B → C every link is the active relationship for its own
   * original, so "Current" can't come from a single flag. A link is current when
   * its replacement chemical was not itself later replaced (it's the live leaf).
   */
  isCurrentReplacementLink(link: { ReplacementDocumentId: number }): boolean {
    const history = this.replacementInfo?.History || [];
    return !history.some(other => other.OriginalDocumentId === link.ReplacementDocumentId);
  }

  openReplaceExisting(): void {
    this.selectedReplacementCandidate = null;
    this.replaceReason = '';
    this.replaceError = null;
    this.replaceCandidateSuggestions = [];
    this.replaceDialogVisible = true;
  }

  /** Drives the replacement-candidate autocomplete; discards stale responses. */
  searchReplacementCandidates(event: { query: string }): void {
    if (!this.record) {
      return;
    }
    const requestId = ++this.replacementSearchRequestId;
    this.sdsApiService
      .getReplacementCandidates(BaseServices.BusinessId, this.record.Id, event?.query || '')
      .subscribe({
        next: results => {
          if (requestId === this.replacementSearchRequestId) {
            this.replaceCandidateSuggestions = results || [];
          }
        },
        error: () => {
          if (requestId === this.replacementSearchRequestId) {
            this.replaceCandidateSuggestions = [];
          }
        }
      });
  }

  confirmAssignReplacement(): void {
    if (!this.record || !this.selectedReplacementCandidate) {
      this.replaceError = this.translate.instant('SDS_REPLACEMENT_SELECT_REQUIRED');
      return;
    }

    this.replaceSaving = true;
    this.replaceError = null;

    // The reason is stored verbatim as the substitution-assessment note, so the
    // default has to be localized here — the backend has no language context.
    // get() rather than instant(), so a not-yet-loaded language bundle can't
    // persist the English fallback for a Norwegian user.
    this.translate
      .get('SDS_SUBST_NOTE_REPLACEMENT')
      .subscribe(defaultReason => this.assignReplacement(
        this.replaceReason?.trim() || defaultReason
      ));
  }

  private assignReplacement(reason: string): void {
    this.sdsApiService
      .assignReplacement(
        BaseServices.BusinessId,
        this.record!.Id,
        this.selectedReplacementCandidate!.DocumentId,
        reason
      )
      .subscribe({
        next: () => {
          this.replaceSaving = false;
          this.replaceDialogVisible = false;
          this.messageService.add({
            severity: 'success',
            summary: 'SDS',
            detail: this.translate.instant('SDS_REPLACEMENT_SUCCESS')
          });
          // Reload so the archived state + replacement history reflect the change.
          this.loadRecord(this.record!.SelectedFileVersionId ?? null);
        },
        error: err => {
          this.replaceSaving = false;
          this.replaceError =
            err?.error?.errors?.ReplacementDocumentId?.[0] ||
            err?.error?.title ||
            err?.error?.detail ||
            this.translate.instant('SDS_REPLACEMENT_FAILED');
        }
      });
  }

  closeReplaceDialog(): void {
    if (this.replaceSaving) {
      return;
    }
    this.replaceDialogVisible = false;
  }

  /** Opens the create wizard pre-flagged to link the new SDS as this chemical's replacement. */
  replaceWithNew(): void {
    if (!this.record) {
      return;
    }
    // The detail route is `detail/:id` (two URL segments), so navigate relative to
    // the parent (`safety-data-sheets`) to reach the sibling `new` route reliably.
    this.router.navigate(['new'], {
      relativeTo: this.route.parent,
      queryParams: { replaces: this.record.Id }
    });
  }

  goBack(): void {
    // Always return to the chemicals list — the record can be reached from the
    // dashboard too, where location.back() would bounce the user to the dashboard.
    // Archived records go back to the archived tab they came from.
    this.router.navigate([this.readOnly ? 'archived' : 'list'], {
      relativeTo: this.route.parent
    });
  }
}
